/* eslint-disable react/no-children-prop */
import React, { useState, useMemo, useCallback } from 'react';
import { useRecoilState } from 'recoil';
import { itemList } from '../../utils/itemList';
import './AllItems.css';
import { Box, Grid, Image } from '@chakra-ui/react';
import { filterItem } from '../../helper/helperFunctions';
import { usePlayerField } from '../../hooks/hooks';
import { IdlescapeButton, IdlescapeInput, IdlescapeWrappingTooltip } from '@idlescape/ui';
import { IoSettingsSharp } from 'react-icons/io5';
import useIsMobile from '../../hooks/useIsMobile';
import { itemsIds } from '../../utils/lookup-dictionaries/lookupItemList';
import { CurrencyPanel } from './CurrencyPanel';
import { IItem } from '../../../../game-server/src/modules/items/items.interface';
import { getSocket } from '../../services/socket.service';
import InventoryItem from './Inventory/InventoryItem';
import { MULTI_MOVE_COOLDOWN } from '../../utils/constantsCollection';
import { TTrueInventoryType } from '../../../../game-server/src/modules/items/Inventory.interface';
import TabEditor from './Inventory/TabEditor';
import { HiMiniLockClosed, HiMiniLockOpen } from 'react-icons/hi2';
import { inventoryFiltersAtom } from '../../atoms/inventoryFilterAtom';

// Standard button styling
const utilityButtonStyle = {
	margin: 'auto',
	width: '100%',
	height: '100%',
	padding: '0 1px',
	size: 'large' as const,
	fontSize: '1rem',
};

export default function AllItems({
	inventoryName = 'stockpile',
	hideSorting = false,
	hideCurrency = false,
	defaultSearch,
}: {
	inventoryName?: TTrueInventoryType;
	hideSorting?: boolean;
	hideCurrency?: boolean;
	defaultSearch?: string;
}) {
	// State management
	const [filters, setFilters] = useRecoilState(inventoryFiltersAtom);
	const [sort, setSort] = useState(defaultSearch ?? '');
	const [settingsVisible, setIsSettingsVisible] = useState(false);
	const [multiSelect, setMultiSelect] = useState(false);
	const [multiSelectDisable, setMultiSelectDisable] = useState(false);
	const [selectedItemIds, setSelectedItemIds] = useState<number[]>([]);
	const [activeInventory, setActiveInventory] = useState(inventoryName);
	const [activeInventoryTabs, setActiveInventoryTabs] = useState<number[]>([]);

	// Hooks
	const isMobile = useIsMobile();
	const playerSettings = usePlayerField('settings');
	const bonusInventoryTabs = usePlayerField('bonusInventoryTabs');
	const currentInventory = usePlayerField(activeInventory);
	const socket = getSocket();

	const showCurrency = playerSettings.miscellaneous.showCurrencyAsItems;
	const tabRules = playerSettings.miscellaneous.inventoryTabRules;
	const tabNames = playerSettings.miscellaneous.inventoryTabNames;
	const tabOrder = useMemo(() => {
		const order = playerSettings.miscellaneous.inventoryTabOrder;
		const count = bonusInventoryTabs + 2;
		return order.length === count ? order : Array.from({ length: count }, (_, i) => i);
	}, [playerSettings.miscellaneous.inventoryTabOrder, bonusInventoryTabs]);
	const tabCount = bonusInventoryTabs + 2;
	// I have accidentally inverted it but I do not want to fix it lol
	const hideInventoryFiltersByDefault = !playerSettings.miscellaneous.showInventoryFiltersByDefault;

	// Create filter string only when dependencies change
	const filterString = useMemo(() => {
		let result = sort;
		if (result.length > 0) {
			result += '|';
		}

		const enabledFilters = filters
			.filter((filter) => filter.enabled)
			.map((filter) => filter.filter)
			.join('|');

		let combined = result + enabledFilters;

		if (combined.endsWith('|')) {
			combined = combined.slice(0, -1);
		}

		return combined;
	}, [sort, filters]);

	// Filter inventory items based on current criteria
	const filterInventory = useCallback(
		(item: IItem) => {
			// Show currency if the item is a currency in the vault, or showCurrency is true
			if (item.itemID <= 49 && !showCurrency && activeInventory !== 'vault') return false;
			return filterItem(item, filterString);
		},
		[filterString, showCurrency, activeInventory]
	);

	// Process inventory items into tabs - memoized
	const tabbedItems = useMemo(() => {
		const sortedInventory = currentInventory.filter(filterInventory);
		const useActiveTabs = activeInventoryTabs.length > 0;
		const result: IItem[][] = [];

		// Group items by tab
		for (const item of sortedInventory) {
			const tab = tabRules[item.itemID] ?? 0;
			if (useActiveTabs && !activeInventoryTabs.includes(tab)) {
				continue;
			}

			if (!result[tab]) {
				result[tab] = [];
			}
			result[tab].push(item);
		}

		return result;
	}, [currentInventory, filterInventory, tabRules, activeInventoryTabs]);

	// Event handlers - memoized
	const toggleFilter = useCallback(
		(index: number) => {
			setFilters((prevFilters) => {
				const newFilters = [...prevFilters];

				// If it's the last filter (clear), disable all filters
				if (index === newFilters.length - 2) {
					return newFilters.map((filter) => ({ ...filter, enabled: false }));
				}
				// If it's the last filter (settings), emit a socket event to change the setting for this
				if (index === newFilters.length - 1) {
					socket.emit('settings:update', {
						value: hideInventoryFiltersByDefault,
						path: 'miscellaneous.showInventoryFiltersByDefault',
					});
					return newFilters;
				}

				// Toggle just this filter
				newFilters[index] = { ...newFilters[index], enabled: !newFilters[index].enabled };
				return newFilters;
			});
		},
		[hideInventoryFiltersByDefault, setFilters]
	);

	const toggleSettings = useCallback(() => {
		setIsSettingsVisible((prev) => {
			if (prev) {
				setSelectedItemIds([]);
				setMultiSelect(false);
			}
			return !prev;
		});
	}, []);

	const toggleVault = useCallback(() => {
		setActiveInventory((prev) => (prev === 'vault' ? 'stockpile' : 'vault'));
	}, []);

	const toggleTab = useCallback((tab: number) => {
		setActiveInventoryTabs((prev) => (prev.includes(tab) ? prev.filter((t) => t !== tab) : [...prev, tab]));
	}, []);

	const disableMultiSelect = useCallback(() => {
		setSelectedItemIds([]);
		setMultiSelect(false);
	}, []);

	const utilityMultiSelect = useCallback(() => {
		setMultiSelect((prev) => {
			if (prev) {
				setSelectedItemIds([]);
			}
			return !prev;
		});
	}, []);

	const multiSelectItem = useCallback((itemId: IItem['id']) => {
		setSelectedItemIds((prev) => {
			const found = prev.indexOf(itemId);
			return found !== -1 ? prev.filter((i) => i !== itemId) : [...prev, itemId];
		});
		return true;
	}, []);

	const multiVaultToggle = useCallback(() => {
		if (selectedItemIds.length === 0) {
			disableMultiSelect();
			return;
		}

		socket.emit('inventory:vaultToggle:batch', {
			inventoryItemIDs: selectedItemIds,
			sourceInventory: activeInventory,
		});

		disableMultiSelect();
		setMultiSelectDisable(true);
		setTimeout(() => setMultiSelectDisable(false), MULTI_MOVE_COOLDOWN);
	}, [selectedItemIds, activeInventory, socket, disableMultiSelect]);

	const multiSelectAll = useCallback(() => {
		const items = currentInventory.filter(filterInventory);
		setSelectedItemIds(items.map((i) => i.id));
	}, [currentInventory, filterInventory]);

	const multiRemoveAll = useCallback(() => {
		setSelectedItemIds([]);
	}, []);

	const multiSendTab = useCallback(
		(tab: number) => {
			socket.emit('inventory:globalTab', {
				inventoryItemID: selectedItemIds,
				tab,
			});
			disableMultiSelect();
		},
		[selectedItemIds, socket, disableMultiSelect]
	);

	const utilitySellJunk = useCallback(() => {
		const commandToSend = {
			channelId: 1, // general
			commandString: '/selljunk',
		};
		socket.emit('chat:command:send', commandToSend);
	}, [socket]);

	const utilityEmptyFood = useCallback(() => {
		socket.emit('inventory:emptyFoodInventory');
	}, [socket]);

	const utilityUnequipAll = useCallback(() => {
		socket.emit('inventory:unequipAll');
	}, [socket]);

	// Component rendering functions
	const renderTab = useCallback(
		(tab: number, index: number, items: IItem[], renameTokenId?: number) => {
			return (
				<Box key={tab}>
					{index !== 0 && <hr />}
					{tab !== 0 && (
						<TabEditor
							tab={Number(tab)}
							allowEdit={settingsVisible}
							allowUp={index !== 0}
							allowDown={index !== tabCount - 1}
						/>
					)}
					<div className='all-items' data-tab={tab}>
						{items.map((item) => {
							if (!itemList[item.itemID]) return null;
							const selected = selectedItemIds.includes(item.id);
							const boxShadow = selected
								? '0 0 5px 3px rgb(208, 108, 238), inset 0 0 2px 5px rgb(208, 108, 238)'
								: undefined;

							return (
								<InventoryItem
									key={`${activeInventory}-${item.id}`}
									inventoryName={activeInventory}
									item={item}
									renameTokenId={renameTokenId}
									overrideOnClick={multiSelect ? () => multiSelectItem(item.id) : undefined}
									boxShadow={boxShadow}
									updaterList={[multiSelect, selectedItemIds.length]}
								/>
							);
						})}
					</div>
				</Box>
			);
		},
		[activeInventory, multiSelect, multiSelectItem, selectedItemIds, settingsVisible, tabCount]
	);

	const renderItems = useCallback(() => {
		const renameTokenId =
			activeInventory === 'stockpile'
				? currentInventory.find((item) => item.itemID === itemsIds.item_name_token)?.id
				: undefined;

		let index = -1;
		return (
			<div className='inventory-container'>
				<div className='multi-tabs'>
					{tabOrder.map((tab) => {
						if (!tabbedItems[tab]) return null;
						index++;
						return renderTab(tab, index, tabbedItems[tab], renameTokenId);
					})}
					{Object.keys(tabbedItems).length === 0 && 'No items found.'}
				</div>
			</div>
		);
	}, [currentInventory, tabOrder, tabbedItems, activeInventory, renderTab]);

	const renderSorting = useCallback(() => {
		if (hideSorting) return null;

		return (
			<div className='inventory-search-container'>
				<IdlescapeInput
					variant='search'
					setValue={setSort}
					value={sort}
					type='search'
					className='anchor-inventory-filter'
				/>
				<IdlescapeButton
					{...utilityButtonStyle}
					fontSize='1.8rem'
					onClick={toggleSettings}
					variant={settingsVisible ? 'green' : 'red'}
				>
					<IoSettingsSharp />
				</IdlescapeButton>
				<IdlescapeButton {...utilityButtonStyle} fontSize='1.8rem' onClick={toggleVault}>
					{activeInventory === 'vault' ? <HiMiniLockClosed /> : <HiMiniLockOpen />}
				</IdlescapeButton>
			</div>
		);
	}, [
		hideSorting,
		sort,
		settingsVisible,
		activeInventory,
		hideInventoryFiltersByDefault,
		toggleSettings,
		toggleVault,
	]);

	const renderUtility = useCallback(() => {
		return (
			<Grid
				gridTemplateColumns='repeat(auto-fit, minmax(100px, 1fr))'
				justifyContent='space-between'
				alignItems='center'
				marginBottom='10px'
				columnGap='5px'
			>
				<IdlescapeButton {...utilityButtonStyle} onClick={utilitySellJunk} variant='green'>
					Sell Junk
				</IdlescapeButton>
				<IdlescapeButton {...utilityButtonStyle} onClick={utilityEmptyFood} variant='green'>
					Empty Food Inv
				</IdlescapeButton>
				<IdlescapeButton {...utilityButtonStyle} onClick={utilityUnequipAll} variant='green'>
					Unequip All
				</IdlescapeButton>
				<IdlescapeButton
					{...utilityButtonStyle}
					onClick={utilityMultiSelect}
					variant={multiSelectDisable ? 'disabled' : 'green'}
				>
					{multiSelectDisable ? 'Please Wait...' : 'Multi-Select'}
				</IdlescapeButton>
			</Grid>
		);
	}, [utilityEmptyFood, utilityMultiSelect, utilitySellJunk, utilityUnequipAll, multiSelectDisable]);

	const renderMultiSelect = useCallback(() => {
		return (
			<Grid
				gridTemplateColumns='repeat(auto-fit, minmax(100px, 1fr))'
				justifyContent='space-between'
				alignItems='center'
				marginBottom='10px'
				columnGap='5px'
			>
				<IdlescapeButton {...utilityButtonStyle} onClick={multiVaultToggle} variant='blue'>
					Move to {activeInventory === 'stockpile' ? 'Vault' : 'Inventory'}
				</IdlescapeButton>
				<IdlescapeButton {...utilityButtonStyle} onClick={multiSelectAll} variant='green'>
					Select All
				</IdlescapeButton>
				<IdlescapeButton {...utilityButtonStyle} onClick={multiRemoveAll} variant='orange'>
					Remove All
				</IdlescapeButton>
				<IdlescapeButton {...utilityButtonStyle} onClick={utilityMultiSelect} variant='red'>
					Exit Selection
				</IdlescapeButton>
			</Grid>
		);
	}, [activeInventory, multiRemoveAll, multiSelectAll, multiVaultToggle, utilityMultiSelect]);

	const renderFilters = useCallback(() => {
		return (
			<Grid
				gridTemplateColumns='repeat(auto-fit, minmax(30px, 1fr))'
				justifyContent='space-between'
				alignItems='center'
				columnGap='7px'
			>
				{filters.map((filter, index) => (
					<IdlescapeWrappingTooltip
						content={`${
							filter.tooltipOverride
								? filter.tooltipOverride
								: filter.enabled
								? 'Filter Active - ' + `${filter.alt.charAt(0).toUpperCase()}${filter.alt.slice(1)}`
								: 'Filter Inactive - ' + `${filter.alt.charAt(0).toUpperCase()}${filter.alt.slice(1)}`
						}`}
						key={`${filter.alt}-tooltip`}
					>
						<IdlescapeButton
							margin='auto'
							height='50px'
							width='100%'
							padding='10% 1px'
							size='medium'
							onClick={() => toggleFilter(index)}
							variant={filter.enabled ? 'green' : 'red'}
						>
							<Image
								src={filter.image}
								alt={filter.alt}
								maxHeight='75%'
								maxWidth='75%'
								margin='auto'
								objectFit='contain'
								filter='drop-shadow(3px 3px 2px rgba(0, 0, 0, 0.9))'
							/>
						</IdlescapeButton>
					</IdlescapeWrappingTooltip>
				))}
			</Grid>
		);
	}, [hideInventoryFiltersByDefault, filters, toggleFilter]);

	const renderSettings = useCallback(() => {
		if (!settingsVisible) return null;

		return (
			<div className='inventory-settings'>
				{multiSelect ? renderMultiSelect() : renderUtility()}
				{hideInventoryFiltersByDefault && renderFilters()}
				{renderTabs()}
			</div>
		);
	}, [multiSelect, renderMultiSelect, renderUtility, settingsVisible, hideInventoryFiltersByDefault, renderFilters]);

	const renderStandaloneFilters = useCallback(() => {
		if (hideSorting) return null;
		if (hideInventoryFiltersByDefault) return null;

		return <div className='inventory-filters'>{renderFilters()}</div>;
	}, [hideInventoryFiltersByDefault, renderFilters]);

	const renderTabs = useCallback(() => {
		// Only show tabs for vault/stockpile
		if (activeInventory !== 'vault' && activeInventory !== 'stockpile') return null;

		return (
			<Grid
				display='grid'
				gridTemplateColumns='repeat(auto-fill, 100px)'
				justifyContent='space-between'
				alignItems='center'
			>
				{tabOrder.map((tab) => {
					// Skip tab 0 when not in multi-select mode
					if (tab === 0 && !multiSelect) return null;

					let onClick = () => toggleTab(tab);
					let variant = activeInventoryTabs.includes(tab) ? 'green' : 'gray';

					if (multiSelect) {
						onClick = () => multiSendTab(tab);
						variant = 'blue';
					}

					const tabName = tabNames[tab] ?? `Tab ${tab}`;
					return (
						<IdlescapeButton
							key={tab}
							margin='auto'
							padding='0 30px'
							size='small'
							onClick={onClick}
							variant={variant}
						>
							{tabName}
						</IdlescapeButton>
					);
				})}
			</Grid>
		);
	}, [activeInventory, activeInventoryTabs, multiSelect, multiSendTab, tabNames, tabOrder, toggleTab]);

	const renderCurrency = useCallback(() => {
		if (hideCurrency) return null;

		// On mobile, always show currency
		if (!isMobile && activeInventory !== 'vault' && activeInventory !== 'stockpile') {
			return null;
		}

		return <CurrencyPanel setFilter={setSort} />;
	}, [hideCurrency, isMobile, activeInventory]);

	// Main render
	const currency = renderCurrency();
	const sorting = renderSorting();
	const settings = renderSettings();
	const standaloneFilters = renderStandaloneFilters();
	const items = renderItems();

	// Count visible sections for grid template
	const visibleSections = [currency, sorting, standaloneFilters, settings].filter(Boolean).length;

	return (
		<Grid
			className='inventory-panel'
			gridTemplateRows={`repeat(${visibleSections}, max-content) minmax(150px, 1fr)`}
		>
			{currency}
			{sorting}
			{standaloneFilters}
			{settings}
			{items}
		</Grid>
	);
}
